import type { ColumnDef } from '@tanstack/react-table'
import { ChevronRight, Download, MoreHorizontal } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Fact, Facts } from 'shared/components/facts'
import { PageFrame } from 'shared/components/page-frame'
import { CompactTable, EmptyTableBody, SectionTitle, Stat, Stats } from 'shared/components/section'
import { ToneNote } from 'shared/components/tone-banner'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Label } from 'shared/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'shared/components/ui/sheet'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { Textarea } from 'shared/components/ui/textarea'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { WHOLE, sum } from 'shared/lib/money'
import { cn } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'
import { saveBase64File } from 'pages/cashdesk/ui/per-kg-file'
import {
	SHADOW_CAUSES,
	type ShadowCause,
	type ShadowDifference,
	type ShadowFeed,
	type ShadowFile,
	type ShadowGapRow,
	type ShadowNight,
	type ShadowNights,
	dayWords,
	explainFailureKey,
	useSideWords,
} from './shadow-model'

/**
 * «Тень» — the admin's screen of the Shadow (ADR-0044, ticket 22).
 *
 * On top, whether something is wrong now: a feed that is not set up or a pull that
 * failed, in the server's words; then the figures — the clean-day count against its
 * goal of 14, the differences nobody has explained yet, what `back` has sent and the
 * translator's gaps. Below, the nights as one table; a click on a night opens it in a
 * side panel — its Points with differences and each difference with what both sides
 * say, explained by one of the three causes with a comment, in a dialog. A night is
 * clean once all of its differences are. The translator's gaps by reason sit under
 * the nights; a reason opens its events. The feed's every count and moment wait in
 * the header's «⋯»; the reports download as Excel from the header.
 *
 * Every call is injected (`ShadowCalls`) so the screen is tested without a backend;
 * the defaults are the real tRPC client and the browser.
 */

export interface ShadowCalls {
	loadFeed: () => Promise<ShadowFeed>
	loadGaps: (reason: string) => Promise<ShadowGapRow[]>
	loadNights: () => Promise<ShadowNights>
	loadNight: (day: string) => Promise<ShadowNight>
	explain: (input: { differenceId: string; cause: ShadowCause; comment: string }) => Promise<unknown>
	loadFile: () => Promise<ShadowFile>
	saveFile: (file: ShadowFile) => void
}

const trpcCalls: ShadowCalls = {
	loadFeed: () => trpcClient.shadow.FeedStatus.query({}),
	loadGaps: (reason) => trpcClient.shadow.Gaps.query({ reason }),
	loadNights: () => trpcClient.shadow.Reconciliations.query({}),
	loadNight: (day) => trpcClient.shadow.ReconciliationDay.query({ day }),
	explain: (input) => trpcClient.shadow.ExplainDifference.mutate(input),
	loadFile: () => trpcClient.shadow.ReconciliationExport.query({}),
	saveFile: saveBase64File,
}

type NightRow = ShadowNights['days'][number]

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

const at = (d: Date | string | null | undefined) => (d ? formatDayTime(d) : null)


export function Shadow(props: Partial<ShadowCalls>) {
	const calls: ShadowCalls = { ...trpcCalls, ...props }
	const { t } = useTranslate()
	const [feed, setFeed] = useState<ShadowFeed | null>(null)
	const [nights, setNights] = useState<ShadowNights | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [openDay, setOpenDay] = useState<string | null>(null)
	const [openGap, setOpenGap] = useState<string | null>(null)
	const [feedOpen, setFeedOpen] = useState(false)
	const [downloading, setDownloading] = useState(false)
	const { loadFeed, loadNights } = calls

	const refresh = useCallback(() => {
		loadFeed().then(setFeed, (e) => setError(failure(e)))
		loadNights().then(setNights, (e) => setError(failure(e)))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(refresh, [refresh])

	const download = async () => {
		setDownloading(true)
		try {
			calls.saveFile(await calls.loadFile())
		} catch (e) {
			setError(failure(e))
		} finally {
			setDownloading(false)
		}
	}

	const unexplained = nights?.days.reduce((n, d) => n + d.unexplained, 0) ?? 0
	const pull = feed?.lastPull ?? null
	// What is wrong with the Shadow's setup — one note, a line each; a failed pull is a fault, apart.
	const setup = feed
		? [
				!feed.shadowMode && { testId: 'shadow-mode', text: t('shadow.shadowModeOff') },
				!feed.feedConfigured && { testId: 'shadow-feed-key', text: t('shadow.feedKeyMissing') },
				!feed.backConfigured && { testId: 'shadow-back-url', text: t('shadow.backUrlMissing') },
			].filter((line): line is { testId: string; text: string } => Boolean(line))
		: []

	return (
		<PageFrame
			title='shadow.title'
			actions={
				<>
					<Button variant='outline' data-testid='shadow-download' disabled={downloading} onClick={download}>
						<Download />
						{downloading ? t('shadow.downloading') : t('shadow.download')}
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='outline' size='icon' aria-label={t('shadow.more')} data-testid='shadow-menu'>
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem onSelect={() => setFeedOpen(true)} data-testid='shadow-feed-open'>
								{t('shadow.feedTitle')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</>
			}
		>
			<div className='flex flex-col gap-6' data-testid='shadow-screen'>
				{error && (
					<p className='text-sm text-destructive' role='alert' data-testid='shadow-error'>
						{error}
					</p>
				)}

				{(feed || nights) && (
					<div className='flex flex-col gap-2 empty:hidden'>
						{feed && setup.length > 0 && (
							<ToneNote
								tone={feed.feedConfigured && feed.backConfigured ? 'attention' : 'refused'}
								title={t('shadow.notConfigured')}
								detail={
									<ul className='flex flex-col gap-0.5'>
										{setup.map((line) => (
											<li key={line.testId} data-testid={line.testId}>
												{line.text}
											</li>
										))}
									</ul>
								}
								testId='shadow-setup'
							/>
						)}
						{pull && !pull.ok && (
							<ToneNote
								tone='refused'
								title={t('shadow.pullFailedTitle')}
								detail={`${at(pull.at)} · ${pull.error ?? ''}`}
								testId='shadow-last-pull'
							/>
						)}
						{nights?.streak.reached && (
							<ToneNote tone='ready' title={t('shadow.streakReached')} testId='shadow-streak-reached' />
						)}
					</div>
				)}

				{feed && nights && (
					<Stats testId='shadow-stats'>
						<Stat
							label={t('shadow.streakTitle')}
							value={t('shadow.streakValue', { days: String(nights.streak.days), goal: String(nights.streak.goal) })}
							valueTestId='shadow-streak'
						/>
						<Stat label={t('shadow.unexplainedAll')} value={unexplained} tone='bad' valueTestId='shadow-unexplained' />
						<Stat label={t('shadow.eventsReceived')} value={feed.eventsReceived} valueTestId='shadow-count-received' />
						<Stat label={t('shadow.gapsAll')} value={feed.translation.gaps} tone='warn' valueTestId='shadow-count-gaps' />
						<Stat label={t('shadow.lastReceived')} value={at(feed.lastReceivedAt) ?? t('shadow.never')} />
					</Stats>
				)}

				{nights && <Nights nights={nights} onOpen={setOpenDay} />}

				{feed && <Gaps feed={feed} onOpen={setOpenGap} />}
			</div>

			<Sheet open={openDay !== null} onOpenChange={(o) => !o && setOpenDay(null)}>
				<SheetContent className='overflow-y-auto data-[side=right]:sm:max-w-4xl'>
					{openDay && <Night day={openDay} calls={calls} onExplained={refresh} />}
				</SheetContent>
			</Sheet>

			<Sheet open={openGap !== null} onOpenChange={(o) => !o && setOpenGap(null)}>
				<SheetContent className='overflow-y-auto data-[side=right]:sm:max-w-2xl'>
					{openGap && feed && <GapSheet reason={openGap} feed={feed} loadGaps={calls.loadGaps} />}
				</SheetContent>
			</Sheet>

			<Sheet open={feedOpen} onOpenChange={setFeedOpen}>
				<SheetContent className='overflow-y-auto data-[side=right]:sm:max-w-xl'>
					{feed && <FeedSheet feed={feed} />}
				</SheetContent>
			</Sheet>
		</PageFrame>
	)
}

/** The nights' reconciliations, newest first: a done night opens in the side panel. */
function Nights({ nights, onOpen }: { nights: ShadowNights; onOpen: (day: string) => void }) {
	const { t } = useTranslate()
	const { streak } = nights
	const rows = useMemo(() => nights.days, [nights.days])

	const columns: ColumnDef<NightRow>[] = [
		{
			id: 'day',
			header: t('shadow.day'),
			cell: ({ row }) => <span className='font-medium tabular-nums'>{dayWords(row.original.day)}</span>,
		},
		{
			id: 'status',
			header: t('shadow.result'),
			cell: ({ row: { original: d } }) => (
				<span
					className={cn(d.status !== 'done' && d.status !== 'running' && 'text-destructive')}
					data-testid={`shadow-night-${d.day}-status`}
				>
					{t(`shadowDayStatus.${d.status}`)}
					{d.problem && ` — ${t(`shadowProblem.${d.problem.reason}`)}`}
				</span>
			),
		},
		{
			id: 'points',
			header: t('shadow.pointsWithDifferences'),
			meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
			cell: ({ row: { original: d } }) => (d.status === 'done' ? d.pointsWithDifferences : '—'),
		},
		{
			id: 'differences',
			header: t('shadow.differences'),
			meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
			cell: ({ row: { original: d } }) => (d.status === 'done' ? d.differences : '—'),
		},
		{
			id: 'unexplained',
			header: t('shadow.unexplained'),
			meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
			cell: ({ row: { original: d } }) =>
				d.status === 'done' ? (
					<span className={cn(d.unexplained > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground')}>
						{d.unexplained}
					</span>
				) : (
					'—'
				),
		},
		{
			id: 'clean',
			header: '',
			cell: ({ row: { original: d } }) => (
				<Badge variant={d.clean ? 'secondary' : 'outline'} data-testid={`shadow-night-${d.day}-clean`}>
					{d.clean ? t('shadow.clean') : t('shadow.notClean')}
				</Badge>
			),
		},
		{
			id: 'open',
			header: '',
			meta: { cellClassName: 'w-px text-muted-foreground' },
			cell: ({ row: { original: d } }) =>
				d.status === 'done' ? (
					<span data-testid={`shadow-night-${d.day}-open`}>
						<ChevronRight className='size-4' />
					</span>
				) : null,
		},
	]

	const { table } = useDataTable({
		data: rows,
		columns,
		rowCount: rows.length,
		...WHOLE,
		getRowId: (d) => d.day,
	})

	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle
				title={t('shadow.nightsTitle')}
				count={nights.days.length}
				hint={t('shadow.intro')}
				aside={
					<span className='text-sm text-muted-foreground' data-testid='shadow-streak-through'>
						{streak.through
							? t('shadow.streakThrough', { day: dayWords(streak.through) })
							: t('shadow.streakNone')}
					</span>
				}
			/>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={t('shadow.nightsEmpty')}
				row={{
					attributes: (d) => ({ 'data-testid': `shadow-night-${d.day}` }),
					onRowClick: (d) => d.status === 'done' && onOpen(d.day),
				}}
			/>
		</section>
	)
}

/** The translator's gaps by reason; a reason opens its events. */
function Gaps({ feed, onOpen }: { feed: ShadowFeed; onOpen: (reason: string) => void }) {
	const { t } = useTranslate()
	const reasonWords = useReasonWords()
	const byReason = feed.translation.gapsByReason
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('shadow.gapsTitle')} count={feed.translation.gaps} hint={t('shadow.gapsHint')} />
			<CompactTable testId='shadow-gaps'>
				<TableHeader>
					<TableRow>
						<TableHead>{t('shadow.gapReason')}</TableHead>
						<TableHead className='text-right'>{t('shadow.gapCount')}</TableHead>
						<TableHead className='w-px' />
					</TableRow>
				</TableHeader>
				{byReason.length === 0 ? (
					<EmptyTableBody columns={3}>{t('shadow.gapsEmpty')}</EmptyTableBody>
				) : (
					<TableBody>
						{byReason.map((g) => (
							<TableRow
								key={g.reason}
								className='cursor-pointer'
								onClick={() => onOpen(g.reason)}
								data-testid={`shadow-gap-${g.reason}`}
							>
								<TableCell className='font-medium'>{reasonWords(g.reason)}</TableCell>
								<TableCell className='text-right tabular-nums' data-testid={`shadow-gap-${g.reason}-count`}>
									{g.count}
								</TableCell>
								<TableCell className='w-px text-muted-foreground'>
									<ChevronRight className='size-4' />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				)}
			</CompactTable>
		</section>
	)
}

function useReasonWords() {
	const { t } = useTranslate()
	return (reason: string) => {
		const key = `shadowGapReason.${reason}`
		const words = t(key as never)
		return words === key || !words ? reason : words
	}
}

/** One reason's gaps: the events `back` sent that the translator could not turn into an act. */
function GapSheet({
	reason,
	feed,
	loadGaps,
}: {
	reason: string
	feed: ShadowFeed
	loadGaps: ShadowCalls['loadGaps']
}) {
	const { t } = useTranslate()
	const reasonWords = useReasonWords()
	const [rows, setRows] = useState<ShadowGapRow[] | null>(null)
	const count = feed.translation.gapsByReason.find((g) => g.reason === reason)?.count ?? 0

	useEffect(() => {
		loadGaps(reason).then(setRows, () => setRows([]))
	}, [loadGaps, reason])

	return (
		<div className='flex flex-col gap-6 pb-6' data-testid={`shadow-gap-${reason}-sheet`}>
			<SheetHeader className='pr-14 pb-0'>
				<SheetTitle className='text-lg'>{reasonWords(reason)}</SheetTitle>
				<SheetDescription>{t('shadow.gapsTitle')}</SheetDescription>
			</SheetHeader>
			<section className='flex flex-col gap-2 px-4'>
				<SectionTitle title={t('shadow.gapEvents')} count={count} />
				<CompactTable>
					<TableHeader>
						<TableRow>
							<TableHead>{t('shadow.gapEvent')}</TableHead>
							<TableHead>{t('shadow.gapTrack')}</TableHead>
							<TableHead>{t('shadow.gapPoint')}</TableHead>
							<TableHead>{t('shadow.gapDetail')}</TableHead>
						</TableRow>
					</TableHeader>
					{rows === null ? (
						<EmptyTableBody columns={4}>{t('shadow.loading')}</EmptyTableBody>
					) : rows.length === 0 ? (
						<EmptyTableBody columns={4}>{t('shadow.gapsEmpty')}</EmptyTableBody>
					) : (
						<TableBody>
							{rows.map((r) => (
								<TableRow key={r.eventId} data-testid='shadow-gap-row'>
									<TableCell>{r.status ?? r.event}</TableCell>
									<TableCell className='font-mono'>{r.trackCode}</TableCell>
									<TableCell>{r.pickupPointId}</TableCell>
									<TableCell className='whitespace-normal text-muted-foreground'>{r.detail}</TableCell>
								</TableRow>
							))}
						</TableBody>
					)}
				</CompactTable>
			</section>
		</div>
	)
}

/** Everything about `back`'s feed: whether it is set up, every count, every moment. */
function FeedSheet({ feed }: { feed: ShadowFeed }) {
	const { t } = useTranslate()
	const pull = feed.lastPull
	const yesNo = (ok: boolean, yes: string, no: string) => (
		<span className={cn(!ok && 'text-destructive')}>{ok ? yes : no}</span>
	)
	return (
		<div className='flex flex-col gap-6 pb-6' data-testid='shadow-feed'>
			<SheetHeader className='pr-14 pb-0'>
				<SheetTitle className='text-lg'>{t('shadow.feedTitle')}</SheetTitle>
				<SheetDescription>{feed.shadowMode ? t('shadow.shadowModeOn') : t('shadow.shadowModeOff')}</SheetDescription>
			</SheetHeader>
			<div className='flex flex-col gap-6 px-4'>
				<Facts>
					<Fact label={t('shadow.eventsReceived')}>{feed.eventsReceived}</Fact>
					<Fact label={t('shadow.translated')}>{feed.translation.translated}</Fact>
					<Fact label={t('shadow.noAct')}>{feed.translation.noAct}</Fact>
					<Fact label={t('shadow.pending')}>{feed.translation.pending}</Fact>
					<Fact label={t('shadow.gaps')}>{feed.translation.gaps}</Fact>
					<Fact label={t('shadow.lastEvent')}>{at(feed.lastEventAt) ?? t('shadow.never')}</Fact>
					<Fact label={t('shadow.lastReceived')}>{at(feed.lastReceivedAt) ?? t('shadow.never')}</Fact>
				</Facts>
				<Facts>
					<Fact label={t('shadow.feedKey')}>
						{yesNo(feed.feedConfigured, t('shadow.feedKeyOk'), t('shadow.feedKeyMissing'))}
					</Fact>
					<Fact label={t('shadow.backUrl')}>
						{yesNo(feed.backConfigured, t('shadow.backUrlOk'), t('shadow.backUrlMissing'))}
					</Fact>
					<Fact label={t('shadow.lastPull')}>
						{!pull
							? t('shadow.never')
							: pull.ok
								? t('shadow.pullOk', {
										at: at(pull.at)!,
										received: String(pull.received),
										duplicates: String(pull.duplicates),
									})
								: yesNo(false, '', t('shadow.pullFailed', { at: at(pull.at)!, error: pull.error ?? '' }))}
					</Fact>
				</Facts>
			</div>
		</div>
	)
}

/** One night in the side panel: its Points with differences, and each difference to explain. */
function Night({ day, calls, onExplained }: { day: string; calls: ShadowCalls; onExplained: () => void }) {
	const { t } = useTranslate()
	const [night, setNight] = useState<ShadowNight | null>(null)
	const [allPoints, setAllPoints] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [explaining, setExplaining] = useState<ShadowDifference | null>(null)
	const { loadNight } = calls

	const read = useCallback(() => {
		loadNight(day).then(setNight, (e) => setError(failure(e)))
	}, [loadNight, day])
	useEffect(read, [read])

	const points = night ? (allPoints ? night.pointRows : night.pointRows.filter((p) => p.differences > 0)) : []

	return (
		<div className='flex flex-col gap-6 pb-6' data-testid={`shadow-night-${day}-detail`}>
			<SheetHeader className='pr-14 pb-0'>
				<SheetTitle className='flex items-center gap-2 text-lg'>
					{t('shadow.nightTitle', { day: dayWords(day) })}
					{night && (
						<Badge variant={night.clean ? 'secondary' : 'outline'}>
							{night.clean ? t('shadow.clean') : t('shadow.notClean')}
						</Badge>
					)}
				</SheetTitle>
				<SheetDescription>{t('shadow.nightHint')}</SheetDescription>
			</SheetHeader>

			{error && <p className='px-4 text-sm text-destructive'>{error}</p>}

			{night && (
				<div className='flex flex-col gap-8 px-4'>
					<Stats>
						<Stat label={t('shadow.points')} value={night.points} />
						<Stat label={t('shadow.pointsWithDifferences')} value={night.pointsWithDifferences} tone='warn' />
						<Stat label={t('shadow.differences')} value={night.differences} />
						<Stat label={t('shadow.unexplained')} value={night.unexplained} tone='bad' />
					</Stats>

					<section className='flex flex-col gap-2'>
						<SectionTitle
							title={allPoints ? t('shadow.allPointsTitle') : t('shadow.pointsTitle')}
							count={points.length}
							aside={
								<Button variant='ghost' size='sm' onClick={() => setAllPoints(!allPoints)} data-testid='shadow-all-points'>
									{allPoints ? t('shadow.onlyWithDifferences') : t('shadow.allPoints', { count: String(night.pointRows.length) })}
								</Button>
							}
						/>
						<PointsTable points={points} />
					</section>

					<section className='flex flex-col gap-2'>
						<SectionTitle title={t('shadow.differencesTitle')} count={night.differenceRows.length} />
						<CompactTable>
							<TableHeader>
								<TableRow>
									<TableHead>{t('shadow.kind')}</TableHead>
									<TableHead>{t('shadow.backSide')}</TableHead>
									<TableHead>{t('shadow.tezgoSide')}</TableHead>
									<TableHead>{t('shadow.explanation')}</TableHead>
								</TableRow>
							</TableHeader>
							{night.differenceRows.length === 0 ? (
								<EmptyTableBody columns={4}>{t('shadow.pointsNone')}</EmptyTableBody>
							) : (
								<TableBody>
									{night.differenceRows.map((d) => (
										<DifferenceRow key={d.differenceId} difference={d} onExplain={() => setExplaining(d)} />
									))}
								</TableBody>
							)}
						</CompactTable>
					</section>
				</div>
			)}

			<ExplainDialog
				difference={explaining}
				explain={calls.explain}
				onClose={() => setExplaining(null)}
				onExplained={() => {
					setExplaining(null)
					read()
					onExplained()
				}}
			/>
		</div>
	)
}

/** A night's Points: what each side has on the shelf and in cash, and how many differences. */
function PointsTable({ points }: { points: ShadowNight['pointRows'] }) {
	const { t } = useTranslate()
	const sideWords = useSideWords()
	const cashCell = (cash: ShadowNight['pointRows'][number]['cash']['back']) => (
		<span title={sideWords('cash', { ...emptySide, cash })}>{sum(cash.payments.uzs - cash.cancellations.uzs)}</span>
	)
	return (
		<CompactTable>
			<TableHeader>
				<TableRow>
					<TableHead>{t('shadow.point')}</TableHead>
					<TableHead className='text-right'>{t('shadow.backParcels')}</TableHead>
					<TableHead className='text-right'>{t('shadow.tezgoParcels')}</TableHead>
					<TableHead className='text-right'>{t('shadow.cashBackSom')}</TableHead>
					<TableHead className='text-right'>{t('shadow.cashTezgoSom')}</TableHead>
					<TableHead className='text-right'>{t('shadow.differences')}</TableHead>
					<TableHead className='text-right'>{t('shadow.unexplained')}</TableHead>
				</TableRow>
			</TableHeader>
			{points.length === 0 ? (
				<EmptyTableBody columns={7}>{t('shadow.pointsNone')}</EmptyTableBody>
			) : (
				<TableBody>
					{points.map((p) => (
						<TableRow key={p.pointId} data-testid={`shadow-point-${p.pointId}`}>
							<TableCell className='font-mono font-semibold'>
								{p.pointId}
								{!p.inBack && (
									<Badge variant='outline' className='ml-2 font-sans font-normal'>
										{t('shadow.notInBack')}
									</Badge>
								)}
							</TableCell>
							<TableCell className='text-right tabular-nums'>{p.backParcels}</TableCell>
							<TableCell className='text-right tabular-nums'>{p.tezgoParcels}</TableCell>
							<TableCell className='text-right tabular-nums'>{cashCell(p.cash.back)}</TableCell>
							<TableCell className='text-right tabular-nums'>{cashCell(p.cash.tezgo)}</TableCell>
							<TableCell className='text-right tabular-nums'>{p.differences}</TableCell>
							<TableCell
								className={cn('text-right tabular-nums', p.unexplained > 0 ? 'font-semibold text-destructive' : 'text-muted-foreground')}
							>
								{p.unexplained}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			)}
		</CompactTable>
	)
}

const emptySide = { pointId: null, state: null, where: null, paid: null, paymentType: null, cash: null }

/** One difference: what kind, both sides in words, and its explanation — or the button to give one. */
export function DifferenceRow({ difference, onExplain }: { difference: ShadowDifference; onExplain: () => void }) {
	const { t } = useTranslate()
	const sideWords = useSideWords()
	const id = difference.differenceId
	const { explanation } = difference
	return (
		<TableRow data-testid={`shadow-difference-${id}`} className='align-top'>
			<TableCell className='whitespace-normal'>
				<div className='font-medium' data-testid={`shadow-difference-${id}-kind`}>
					{t(`shadowKind.${difference.kind}`)}
				</div>
				<div className='text-xs text-muted-foreground'>
					<span className='font-mono'>{difference.pointId}</span>
					{difference.trackCode && <span className='ml-2 font-mono'>{difference.trackCode}</span>}
				</div>
			</TableCell>
			<TableCell className='whitespace-normal' data-testid={`shadow-difference-${id}-back`}>
				{sideWords(difference.kind, difference.back)}
			</TableCell>
			<TableCell className='whitespace-normal' data-testid={`shadow-difference-${id}-tezgo`}>
				{sideWords(difference.kind, difference.tezgo)}
			</TableCell>
			<TableCell className='whitespace-normal'>
				{explanation ? (
					<div className='flex flex-col items-start gap-1' data-testid={`shadow-difference-${id}-explanation`}>
						<Badge variant='secondary'>{t(`shadowCause.${explanation.cause}`)}</Badge>
						<span>{explanation.comment}</span>
						<span className='text-xs text-muted-foreground'>
							{t('shadow.explainedBy', { name: explanation.by.name, at: at(explanation.at)! })}
						</span>
						<Button
							variant='link'
							size='sm'
							className='h-auto p-0'
							onClick={onExplain}
							data-testid={`shadow-difference-${id}-change`}
						>
							{t('shadow.reexplain')}
						</Button>
					</div>
				) : (
					<Button size='sm' variant='outline' onClick={onExplain} data-testid={`shadow-difference-${id}-explain`}>
						{t('shadow.explain')}
					</Button>
				)}
			</TableCell>
		</TableRow>
	)
}

/** A difference explained by one of the three causes and a comment — neither may be left out. */
function ExplainDialog({
	difference,
	explain,
	onClose,
	onExplained,
}: {
	difference: ShadowDifference | null
	explain: ShadowCalls['explain']
	onClose: () => void
	onExplained: () => void
}) {
	const { t } = useTranslate()
	return (
		<Dialog open={difference !== null} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className='sm:max-w-lg'>
				{difference && (
					<ExplainForm key={difference.differenceId} difference={difference} explain={explain} onClose={onClose} onExplained={onExplained} />
				)}
				{!difference && <DialogTitle className='sr-only'>{t('shadow.explain')}</DialogTitle>}
			</DialogContent>
		</Dialog>
	)
}

function ExplainForm({
	difference,
	explain,
	onClose,
	onExplained,
}: {
	difference: ShadowDifference
	explain: ShadowCalls['explain']
	onClose: () => void
	onExplained: () => void
}) {
	const { t } = useTranslate()
	const [cause, setCause] = useState<ShadowCause | ''>(difference.explanation?.cause ?? '')
	const [comment, setComment] = useState(difference.explanation?.comment ?? '')
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const id = difference.differenceId
	const ready = cause !== '' && comment.trim().length > 0

	const save = async () => {
		if (!ready) return
		setSaving(true)
		setError(null)
		try {
			await explain({ differenceId: id, cause, comment: comment.trim() })
			onExplained()
		} catch (e) {
			const key = explainFailureKey(e)
			setError(key ? t(key) : failure(e))
		} finally {
			setSaving(false)
		}
	}

	return (
		<div className='grid gap-4' data-testid={`shadow-difference-${id}-dialog`}>
			<DialogHeader>
				<DialogTitle>{t('shadow.explainTitle')}</DialogTitle>
				<DialogDescription>
					{t(`shadowKind.${difference.kind}`)} · {difference.pointId}
					{difference.trackCode ? ` · ${difference.trackCode}` : ''}
				</DialogDescription>
			</DialogHeader>
			<div className='grid gap-1.5'>
				<Label htmlFor={`cause-${id}`}>{t('shadow.cause')}</Label>
				<Select value={cause} onValueChange={(v) => setCause(v as ShadowCause)}>
					<SelectTrigger id={`cause-${id}`} className='w-full' data-testid={`shadow-difference-${id}-cause`}>
						<SelectValue placeholder={t('shadow.causePlaceholder')} />
					</SelectTrigger>
					<SelectContent>
						{SHADOW_CAUSES.map((c) => (
							<SelectItem key={c} value={c} data-testid={`shadow-cause-${c}`}>
								{t(`shadowCause.${c}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className='grid gap-1.5'>
				<Label htmlFor={`comment-${id}`}>{t('shadow.comment')}</Label>
				<Textarea
					id={`comment-${id}`}
					data-testid={`shadow-difference-${id}-comment`}
					rows={3}
					placeholder={t('shadow.commentHint')}
					value={comment}
					onChange={(e) => setComment(e.target.value)}
				/>
			</div>
			{error && (
				<p role='alert' className='text-sm text-destructive' data-testid={`shadow-difference-${id}-error`}>
					{error}
				</p>
			)}
			<DialogFooter>
				<Button variant='outline' onClick={onClose}>
					{t('shadow.cancel')}
				</Button>
				<Button data-testid={`shadow-difference-${id}-save`} disabled={!ready || saving} onClick={save}>
					{saving ? t('shadow.saving') : t('shadow.explain')}
				</Button>
			</DialogFooter>
		</div>
	)
}
